/**
 * Friday wordmark — the movie logo, re-imagined.
 *
 * Rather than hand-tracing the bitmap (which always ends up with a couple
 * of letters reading as the wrong glyph), we use SVG `<text>` with the
 * heavy display Google Fonts "Bowlby One" + "Bowlby One SC" loaded in
 * index.html. The strokes are faked with `paint-order: stroke fill`
 * + a thick outer stroke + a thinner inner accent fill, both driven by
 * CSS variables so every theme recolours the mark instantly.
 *
 * Three themes carry a wordmark of their own, drawn in the same viewBox so
 * every call site keeps its size:
 *   - cyberpunk:    slanted Orbitron caps in Edgerunners yellow, a cyan and
 *                   pink chromatic split and one glitched slice that jumps
 *                   in a short burst every few seconds.
 *   - blade-runner: wide Syncopate caps with the film title's amber-to-red
 *                   gradient, horizontal slits and a red neon glow.
 *   - balatro:      chunky Pixelify Sans letters, white on a red outline
 *                   with a hard drop shadow, each letter bobbing on its own.
 *
 * Two variants:
 *   - default: full "friday" wordmark
 *   - compact:  stylised "Fr" mark (sidebar / drawer / favicon-ish)
 */

import { useId } from 'react'
import { cn } from '@/lib/utils'
import { useThemeId } from '@/features/theme/ThemeProvider'

interface FridayMarkProps {
  size?: number
  className?: string
  compact?: boolean
  title?: string
}

/**
 * The fake ink outline is drawn in viewBox units, so it scales with the
 * rendered size. Bowlby One is already a very heavy face: at the auth-page
 * size (64) the 12-unit outline reads as inked line work, but on a 30px
 * navbar mark the same outline closes the letter counters and the wordmark
 * turns into a blob.
 *
 * So the outline shrinks faster than the mark does. Size 64 keeps the
 * original 12 units and is pixel-identical to before; smaller marks get
 * proportionally less outline and stay legible.
 */
function outlineWidth(size: number): number {
  const reference = 12 * Math.pow(size / 64, 0.66)
  return Math.round(Math.min(12, Math.max(2.5, reference)) * 10) / 10
}

interface VariantProps {
  compact: boolean
  uid: string
  size: number
}

/** Cyberpunk: Edgerunners-style slanted caps with a chromatic glitch. */
function CyberpunkMark({ compact, uid }: VariantProps) {
  const word = compact ? 'FR' : 'FRIDAY'
  const w = compact ? 200 : 720
  const cx = w / 2
  const len = compact ? 168 : 640
  const top = `${uid}-cp-top`
  const bottom = `${uid}-cp-bottom`
  const common = {
    x: cx,
    y: 146,
    textAnchor: 'middle' as const,
    textLength: len,
    lengthAdjust: 'spacingAndGlyphs' as const,
    className: 'friday-cp-text',
  }
  return (
    <>
      <defs>
        <clipPath id={top}>
          <rect x="0" y="0" width={w} height="98" />
        </clipPath>
        <clipPath id={bottom}>
          <rect x="0" y="98" width={w} height="102" />
        </clipPath>
        <style>{`
          .friday-cp-text {
            font-family: 'Orbitron', 'Chakra Petch', sans-serif;
            font-weight: 900;
            font-size: 128px;
          }
          /* The Edgerunners title glitches in bursts: still for most of
             the cycle, then the split widens and a slice jumps. */
          .friday-cp-split-l { animation: friday-cp-split-l 5.5s steps(1, end) infinite; }
          .friday-cp-split-r { animation: friday-cp-split-r 5.5s steps(1, end) infinite; }
          .friday-cp-slice { animation: friday-cp-slice 5.5s steps(1, end) infinite; }
          @keyframes friday-cp-split-l {
            0%, 86%, 100% { transform: translate(0, 0); }
            88% { transform: translate(-14px, 3px); }
            91% { transform: translate(6px, -2px); }
            94% { transform: translate(-8px, 0); }
          }
          @keyframes friday-cp-split-r {
            0%, 86%, 100% { transform: translate(0, 0); }
            88% { transform: translate(14px, -3px); }
            91% { transform: translate(-6px, 2px); }
            94% { transform: translate(10px, 0); }
          }
          @keyframes friday-cp-slice {
            0%, 86%, 100% { transform: translate(0, 0); }
            88% { transform: translate(-22px, 0); }
            91% { transform: translate(16px, 0); }
            94% { transform: translate(-6px, 0); }
          }
          @media (prefers-reduced-motion: reduce) {
            .friday-cp-split-l, .friday-cp-split-r, .friday-cp-slice { animation: none; }
          }
        `}</style>
      </defs>
      <g transform={`translate(${compact ? 18 : 30} 0) skewX(-12)`}>
        <g className="friday-cp-split-l">
          <text {...common} x={cx - 7} y={151} fill="rgb(var(--cp-cyan, 76 204 230))">
            {word}
          </text>
        </g>
        <g className="friday-cp-split-r">
          <text
            {...common}
            x={cx + 7}
            y={141}
            fill="rgb(var(--cp-pink, 255 141 204))"
            opacity="0.85"
          >
            {word}
          </text>
        </g>
        <text {...common} clipPath={`url(#${top})`} fill="rgb(255 230 41)">
          {word}
        </text>
        <g transform="translate(10 0)">
          <g className="friday-cp-slice">
            <text {...common} clipPath={`url(#${bottom})`} fill="rgb(255 230 41)">
              {word}
            </text>
          </g>
        </g>
      </g>
      {!compact && (
        <polygon points="96,170 560,170 548,182 84,182" fill="rgb(var(--cp-pink, 255 141 204))" />
      )}
      {!compact && <polygon points="572,170 640,170 628,182 560,182" fill="rgb(var(--cp-cyan, 76 204 230))" />}
    </>
  )
}

/** Blade Runner: amber-to-red title caps with slits and a neon glow. */
function BladeRunnerMark({ compact, uid }: VariantProps) {
  const word = compact ? 'FR' : 'FRIDAY'
  const w = compact ? 200 : 720
  const cx = w / 2
  const len = compact ? 176 : 680
  const grad = `${uid}-br-grad`
  const glow = `${uid}-br-glow`
  const slits = `${uid}-br-slits`
  const common = {
    x: cx,
    y: 138,
    textAnchor: 'middle' as const,
    textLength: len,
    lengthAdjust: 'spacingAndGlyphs' as const,
    className: 'friday-br-text',
  }
  return (
    <>
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffd38a" />
          <stop offset="0.42" stopColor="#ff8f3a" />
          <stop offset="0.72" stopColor="#e8432e" />
          <stop offset="1" stopColor="#8a1a10" />
        </linearGradient>
        <filter id={glow} x="-10%" y="-40%" width="120%" height="180%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <mask id={slits} maskUnits="userSpaceOnUse" x="0" y="0" width={w} height="200">
          <rect x="0" y="0" width={w} height="200" fill="white" />
          <rect x="0" y="104" width={w} height="6" fill="black" />
          <rect x="0" y="121" width={w} height="5" fill="black" />
        </mask>
        <style>{`
          .friday-br-text {
            font-family: 'Syncopate', 'Barlow Semi Condensed', sans-serif;
            font-weight: 700;
            font-size: 116px;
          }
        `}</style>
      </defs>
      <text {...common} fill="#e8432e" opacity="0.75" filter={`url(#${glow})`}>
        {word}
      </text>
      <text {...common} fill={`url(#${grad})`} mask={`url(#${slits})`}>
        {word}
      </text>
    </>
  )
}

/** Balatro: chunky pixel letters, white on red, each one bobbing. */
function BalatroMark({ compact }: VariantProps) {
  const letters = compact ? ['F', 'R'] : ['F', 'R', 'I', 'D', 'A', 'Y']
  const w = compact ? 200 : 720
  const step = compact ? 84 : 112
  const start = w / 2 - (step * (letters.length - 1)) / 2
  return (
    <>
      <defs>
        <style>{`
          .friday-bal-text {
            font-family: 'Pixelify Sans', 'Fredoka', sans-serif;
            font-weight: 700;
            font-size: 150px;
            paint-order: stroke fill;
            stroke-linejoin: miter;
          }
          .friday-bal-letter {
            transform-box: fill-box;
            transform-origin: center;
            animation: friday-bal-bob 2.4s ease-in-out infinite;
          }
          @keyframes friday-bal-bob {
            0%, 100% { transform: translateY(0) rotate(-3deg); }
            50% { transform: translateY(-5px) rotate(3deg); }
          }
          @media (prefers-reduced-motion: reduce) {
            .friday-bal-letter { animation: none; }
          }
        `}</style>
      </defs>
      {letters.map((ch, i) => {
        const x = start + i * step
        return (
          <g
            key={i}
            className="friday-bal-letter"
            style={{ animationDelay: `${-i * 0.3}s` }}
          >
            <text x={x + 4} y={168} textAnchor="middle" className="friday-bal-text" fill="#0d1516" stroke="#0d1516" strokeWidth="16">
              {ch}
            </text>
            <text x={x} y={158} textAnchor="middle" className="friday-bal-text" fill="#ffffff" stroke="#fe5f55" strokeWidth="14">
              {ch}
            </text>
          </g>
        )
      })}
    </>
  )
}

const THEMED_MARKS: Partial<Record<string, (p: VariantProps) => JSX.Element>> = {
  cyberpunk: CyberpunkMark,
  'blade-runner': BladeRunnerMark,
  balatro: BalatroMark,
}

export function FridayMark({
  size = 32,
  className,
  compact = false,
  title = 'Friday',
}: FridayMarkProps) {
  const themeId = useThemeId()
  const uid = 'fm' + useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const width = compact ? size : size * 3.6
  const height = compact ? size : size * 1
  const strokeWidth = outlineWidth(size)
  const Themed = THEMED_MARKS[themeId]
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={compact ? '0 0 200 200' : '0 0 720 200'}
      role="img"
      aria-label={title}
      className={cn('friday-mark shrink-0 select-none', className)}
      data-mark={Themed ? themeId : 'default'}
    >
      <title>{title}</title>
      {Themed ? (
        <Themed compact={compact} uid={uid} size={size} />
      ) : (
        <>
          <defs>
            <style>{`
              .friday-text {
                font-family: 'Bowlby One', 'Fredoka', 'Inter', sans-serif;
                font-weight: 900;
                paint-order: stroke fill;
                stroke: var(--brand-stroke, currentColor);
                stroke-linejoin: round;
                stroke-linecap: round;
                fill: var(--brand-fill, currentColor);
                letter-spacing: -0.04em;
              }
              .friday-text.compact {
                font-size: 200px;
              }
              .friday-text.full {
                font-size: 200px;
              }
            `}</style>
          </defs>
          {compact ? (
            <text
              x="100"
              y="160"
              textAnchor="middle"
              className="friday-text compact"
              strokeWidth={strokeWidth}
            >
              Fr
            </text>
          ) : (
            <text
              x="360"
              y="160"
              textAnchor="middle"
              className="friday-text full"
              strokeWidth={strokeWidth}
            >
              friday
            </text>
          )}
        </>
      )}
    </svg>
  )
}
